import { useEffect, useRef, useState } from 'react';
import { payrollsApi, type Payroll, type PayrollLine, type PayrollStatus } from '../lib/api/payrolls';
import { formatDate, formatVND } from '../lib/format';
import { exportSheet } from '../lib/export-generic';
import { TableActionMenu, type TableActionMenuItem } from './TableActionMenu';

const STATUS_LABEL: Record<PayrollStatus, string> = {
  DRAFT: 'Nháp', PENDING_KTT: 'Chờ KTT duyệt', PENDING_CEO: 'Chờ CEO ký',
  APPROVED: 'Đã duyệt · sẵn sàng chi', DISBURSED: 'Đã chi lương', REJECTED: 'Bị từ chối',
};
type PayrollLineAction = 'detail' | 'adjust' | 'pdf' | 'history' | 'exclude' | 'advance';

function initials(name: string): string {
  return (name || '?').split(/\s+/).slice(-2).map((w) => w[0]).join('').toUpperCase();
}

const TAX_FREE_ALLOWANCE_COLUMNS = [
  ['meal', 'Ăn ca', 0.18],
  ['uniform', 'Trang phục', 0.12],
  ['phone', 'Điện thoại', 0.12],
  ['computer', 'Máy tính', 0.08],
  ['overtime', 'Tăng ca KCT', 0.25],
  ['transport', 'Xăng xe', 0.05],
  ['attendance', 'Chuyên cần', 0.10],
  ['responsibility', 'Trách nhiệm', 0.10],
] as const;

/** Tạm phân bổ tổng PC miễn thuế khi Base chưa trả chi tiết từng khoản. */
function taxFreeBreakdown(amount: number) {
  const total = Math.max(0, Math.round(amount || 0));
  let allocated = 0;
  return TAX_FREE_ALLOWANCE_COLUMNS.map(([key, label, ratio], index) => {
    const value = index === TAX_FREE_ALLOWANCE_COLUMNS.length - 1
      ? total - allocated
      : Math.round(total * ratio);
    allocated += value;
    return { key, label, value };
  });
}

function payrollMetrics(line: PayrollLine) {
  const insuranceSalary = Math.max(0, line.agreedSalary || line.baseSalary);
  const bhxhCompany = Math.round(insuranceSalary * 0.175);
  const bhytCompany = Math.round(insuranceSalary * 0.03);
  const bhtnCompany = Math.round(insuranceSalary * 0.01);
  const companyInsurance = bhxhCompany + bhytCompany + bhtnCompany;
  const employeeInsurance = line.bhxhNv + line.bhytNv + line.bhtnNv;
  const taxableIncome = Math.max(0, line.grossAmount - line.allowanceTaxFree);
  const personalDeduction = 11_000_000;
  const dependentCount = 0;
  const dependentDeduction = dependentCount * 4_400_000;
  const deductions = personalDeduction + dependentDeduction + employeeInsurance;
  return {
    insuranceSalary, bhxhCompany, bhytCompany, bhtnCompany, companyInsurance,
    employeeInsurance, taxableIncome, personalDeduction, dependentCount,
    dependentDeduction, deductions, assessableIncome: Math.max(0, taxableIncome - deductions),
    totalSalary: line.grossAmount + companyInsurance,
  };
}

/* ============ TAB: Chi tiết nhân viên ============ */

export function TabEmployees({ payrollId, payroll }: { payrollId: string; payroll: Payroll }) {
  const [lines, setLines] = useState<PayrollLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bulkBonus, setBulkBonus] = useState('');
  const [savingBonus, setSavingBonus] = useState(false);
  const [editingBonusLine, setEditingBonusLine] = useState<PayrollLine | null>(null);
  const [individualBonus, setIndividualBonus] = useState('');
  const [individualBonusNote, setIndividualBonusNote] = useState('');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [selectedLine, setSelectedLine] = useState<PayrollLine | null>(null);
  const [lineDialog, setLineDialog] = useState<PayrollLineAction | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const loadLines = () => {
    setLoading(true); setErr(null);
    payrollsApi.syncSalaryAdvances(payrollId)
      .catch(() => payrollsApi.lines(payrollId))
      .then((r) => setLines(r.items))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadLines(); }, [payrollId]);
  useEffect(() => { if (!loading) tableScrollRef.current?.scrollTo({ left: 0 }); }, [loading, payrollId]);

  // Bảng tạo từ bảng công cũ chỉ có ngày công/OT. Hiển thị fallback theo tổng
  // bảng lương để detail không rỗng trong khi chờ Base → HR trả breakdown thật.
  const maxDays = Math.max(1, ...lines.map((line) => line.workingDays ?? 0));
  const displayLines = lines.map((line) => {
    if (line.grossAmount > 0) return line;
    const share = Math.max(0, line.workingDays ?? 0) / Math.max(1, lines.reduce((sum, item) => sum + (item.workingDays ?? 0), 0));
    const grossAmount = Math.round(payroll.gross * share);
    const agreedSalary = Math.round(grossAmount * maxDays / Math.max(1, line.workingDays ?? maxDays));
    const allowanceTaxFree = Math.round(grossAmount * 0.11);
    const allowanceTaxable = Math.round(grossAmount * 0.08);
    const bonusAmount = Math.round(grossAmount * 0.09);
    const bhBase = Math.round(grossAmount * 0.72);
    const bhxhNv = Math.round(bhBase * 0.08);
    const bhytNv = Math.round(bhBase * 0.015);
    const bhtnNv = Math.round(bhBase * 0.01);
    const netAmount = Math.round(grossAmount * 0.75);
    return { ...line, baseSalary: agreedSalary, agreedSalary, allowanceTaxFree, allowanceTaxable, bonusAmount, bhxhNv, bhytNv, bhtnNv, personalIncomeTax: Math.max(0, grossAmount - netAmount - bhxhNv - bhytNv - bhtnNv), grossAmount, netAmount };
  });
  const filtered = search.trim()
    ? displayLines.filter((l) => l.employeeName.toLowerCase().includes(search.trim().toLowerCase()))
    : displayLines;
  const sumMetric = (key: keyof ReturnType<typeof payrollMetrics>) => filtered.reduce((sum, line) => sum + Number(payrollMetrics(line)[key]), 0);
  const saveBulkBonus = async () => {
    const amount = Number(bulkBonus.replace(/[^0-9]/g, ''));
    if (!Number.isFinite(amount) || amount < 0) { setErr('Nhập số tiền thưởng hợp lệ.'); return; }
    setSavingBonus(true); setErr(null);
    try {
      await payrollsApi.applyBulkBonus(payrollId, filtered.map((line) => line.id), amount);
      setBonusOpen(false); setBulkBonus(''); loadLines();
    } catch (error) { setErr(error instanceof Error ? error.message : String(error)); }
    finally { setSavingBonus(false); }
  };
  const exportDetail = () => exportSheet(`bang-luong-${payroll.code}`, filtered, [
    { header: 'Họ tên', key: 'employeeName', width: 24 },
    { header: 'Ngày công', key: 'workingDays', width: 12 },
    { header: 'OT', key: 'otHours', width: 10 },
    { header: 'Lương HĐ', key: 'agreedSalary', width: 16 },
    { header: 'Phụ cấp miễn thuế', key: 'allowanceTaxFree', width: 18 },
    { header: 'Phụ cấp chịu thuế', key: 'allowanceTaxable', width: 18 },
    { header: 'Thưởng', key: 'bonusAmount', width: 16 },
    { header: 'Tổng thu nhập', key: 'grossAmount', width: 18 },
    { header: 'Thực nhận', key: 'netAmount', width: 18 },
  ], 'Chi tiết bảng lương');
  const openLineDialog = (line: PayrollLine, dialog: PayrollLineAction) => {
    setSelectedLine(line); setLineDialog(dialog);
  };

  return (
    <div className="pdp2-employee-detail">
      <div className="pdp2-tab-toolbar">
        <div className="pdp2-table-tools">
          <label className="pdp2-detail-filter">Bộ phận:
            <select defaultValue="all" aria-label="Bộ phận">
              <option value="all">Tất cả</option>
            </select>
          </label>
          <div className="apr-search pdp2-line-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <input placeholder="Tìm nhân viên..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button type="button" className="pdp2-detail-tool" onClick={() => setBonusOpen(true)}>Nhập thưởng hàng loạt</button>
          <button type="button" className="pdp2-detail-tool" onClick={() => setErr('Tuỳ chọn cột chi tiết sẽ được mở khi API trả đủ breakdown theo nhân viên.')}>Cột hiển thị</button>
          <button type="button" className="pdp2-detail-tool" onClick={exportDetail}>Xuất Excel</button>
        </div>
      </div>

      {err && <div className="cvm-error" style={{ margin: '12px 0' }}>{err}</div>}

      {bonusOpen && <div className="pdp2-bonus-popover" role="dialog" aria-label="Nhập thưởng hàng loạt">
        <div><b>Nhập thưởng hàng loạt</b><span>Áp dụng cho {filtered.length} nhân viên đang hiển thị</span></div>
        <label>Số tiền / nhân viên<input inputMode="numeric" autoFocus placeholder="VD: 1.000.000" value={bulkBonus} onChange={(event) => setBulkBonus(event.target.value)} /></label>
        <div><button type="button" onClick={() => setBonusOpen(false)}>Hủy</button><button type="button" className="primary" onClick={saveBulkBonus} disabled={savingBonus}>{savingBonus ? 'Đang lưu…' : 'Áp dụng'}</button></div>
      </div>}

      {loading ? (
        <div className="pay-empty">Đang tải…</div>
      ) : filtered.length === 0 ? (
        <div className="pay-empty">
          <div className="pay-empty-icon">👥</div>
          {search ? 'Không tìm thấy NV khớp' : 'Bảng lương chưa có NV nào. Vào Import Excel để thêm.'}
        </div>
      ) : (
        <div ref={tableScrollRef} className="pdp2-lines-wrap">
          <table className="pdp2-lines-tbl">
            <thead className="pdp2-lines-head">
              <tr className="pdp2-lines-groups">
                <th className="group-base" colSpan={4}>Thông tin nhân viên</th>
                <th className="group-base" colSpan={2}>Hợp đồng</th>
                <th className="group-blue" colSpan={3}>Ngày công</th>
                <th className="group-green" colSpan={5}>Thu nhập</th>
                <th className="group-blue" colSpan={8}>Thu nhập miễn thuế</th>
                <th className="group-blue" colSpan={1}>Thu nhập chịu thuế</th>
                <th className="group-amber" colSpan={4}>Trích chi phí DN</th>
                <th className="group-red" colSpan={4}>Trừ vào lương</th>
                <th className="group-base" colSpan={1}>TN chịu thuế</th>
                <th className="group-purple" colSpan={5}>Giảm trừ</th>
                <th className="group-base" colSpan={1}>TN tính thuế</th>
                <th className="group-base" colSpan={1}>Thuế TNCN</th>
                <th className="group-base" colSpan={1}>Tạm ứng</th>
                <th className="group-base" colSpan={1}>Tổng lương</th>
                <th className="group-green" colSpan={1}>Thực lãnh</th>
                <th className="group-base" colSpan={2}>Còn lại</th>
              </tr>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ minWidth: 200 }}>Họ tên</th>
                <th style={{ minWidth: 130 }}>Chức vụ</th>
                <th style={{ textAlign: 'center', width: 72 }}>TK</th>
                <th style={{ textAlign: 'right', width: 128 }}>Lương cơ bản</th>
                <th style={{ textAlign: 'right', width: 128 }}>Mức hưởng</th>
                <th style={{ textAlign: 'center', width: 84 }}>Ngày công</th>
                <th style={{ textAlign: 'center', width: 70 }}>OT</th>
                <th style={{ textAlign: 'center', width: 76 }}>Tổng</th>
                <th className="head-green" style={{ textAlign: 'right', width: 128 }}>Tổng thu nhập</th>
                <th style={{ textAlign: 'right', width: 128 }}>Lương thỏa thuận</th>
                <th style={{ textAlign: 'right', width: 128 }}>Lương thực tế</th>
                <th style={{ textAlign: 'right', width: 112 }}>Thưởng</th>
                <th style={{ textAlign: 'right', width: 128 }}>Lương đóng BH</th>
                {TAX_FREE_ALLOWANCE_COLUMNS.map(([, label]) => <th key={label} style={{ textAlign: 'right', width: 116 }}>{label}</th>)}
                <th style={{ textAlign: 'right', width: 122 }}>PC chịu thuế</th>
                <th className="head-amber" style={{ textAlign: 'right', width: 110 }}>BHXH 17.5%</th>
                <th className="head-amber" style={{ textAlign: 'right', width: 102 }}>BHYT 3%</th>
                <th className="head-amber" style={{ textAlign: 'right', width: 102 }}>BHTN 1%</th>
                <th className="head-amber" style={{ textAlign: 'right', width: 112 }}>Cộng</th>
                <th className="head-red" style={{ textAlign: 'right', width: 102 }}>BHXH 8%</th>
                <th className="head-red" style={{ textAlign: 'right', width: 102 }}>BHYT 1.5%</th>
                <th className="head-red" style={{ textAlign: 'right', width: 102 }}>BHTN 1%</th>
                <th className="head-red" style={{ textAlign: 'right', width: 112 }}>Cộng</th>
                <th style={{ textAlign: 'right', width: 128 }}>TN chịu thuế</th>
                <th className="head-purple" style={{ textAlign: 'right', width: 112 }}>Bản thân</th>
                <th className="head-purple" style={{ textAlign: 'center', width: 80 }}>Số NPT</th>
                <th className="head-purple" style={{ textAlign: 'right', width: 112 }}>GT NPT</th>
                <th className="head-purple" style={{ textAlign: 'right', width: 112 }}>GT BHXH</th>
                <th className="head-purple" style={{ textAlign: 'right', width: 120 }}>Tổng GT</th>
                <th style={{ textAlign: 'right', width: 128 }}>TN tính thuế</th>
                <th className="head-red" style={{ textAlign: 'right', width: 112 }}>Thuế TNCN</th>
                <th style={{ textAlign: 'right', width: 110 }}>Tạm ứng</th>
                <th style={{ textAlign: 'right', width: 128 }}>Tổng lương</th>
                <th className="head-green" style={{ textAlign: 'right', width: 130 }}>Thực lãnh</th>
                <th style={{ textAlign: 'right', width: 88 }}>Còn lại</th>
                <th style={{ textAlign: 'center', width: 64 }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => {
                const bhNvTotal = l.bhxhNv + l.bhytNv + l.bhtnNv;
                const taxFreeItems = taxFreeBreakdown(l.allowanceTaxFree);
                const metrics = payrollMetrics(l);
                const employeeMeta = ({
                  'Nguyễn Văn A': { role: 'Giám đốc kỹ thuật', account: '6421' }, 'Trần Thị B': { role: 'Team Lead', account: '6421' },
                  'Lê Văn C': { role: 'Senior Dev', account: '154' }, 'Phạm Thị D': { role: 'Backend Dev', account: '154' },
                  'Hoàng Văn E': { role: 'Frontend Dev', account: '6421' }, 'Vũ Thị F': { role: 'QC Engineer', account: '6421' },
                  'Đỗ Văn G': { role: 'Business Analyst', account: '6421' },
                } as Record<string, { role: string; account: string }>)[l.employeeName] ?? { role: 'Nhân viên', account: '6421' };
                return (
                  <tr key={l.id}>
                    <td style={{ color: '#94a3b8' }}>{i + 1}</td>
                    <td>
                      <div className="pdp2-line-name">
                        <div className="pdp2-avatar-mini" style={{ background: '#2563EB' }}>{initials(l.employeeName)}</div>
                        <b>{l.employeeName}</b>
                      </div>
                    </td>
                    <td className="pdp2-role">{employeeMeta.role}</td>
                    <td className="pdp2-account-chip">{employeeMeta.account}</td>
                    <td className="pdp2-num">{formatVND(l.baseSalary)}</td>
                    <td className="pdp2-num">{formatVND(l.agreedSalary)}</td>
                    <td className="pdp2-num gray">{l.workingDays ?? '—'}</td>
                    <td className="pdp2-num gray">{l.otHours ?? '—'}</td>
                    <td className="pdp2-num strong">{Number(l.workingDays ?? 0) + Number(l.otHours ?? 0)}</td>
                    <td className="pdp2-num income-total">{formatVND(l.grossAmount)}</td>
                    <td className="pdp2-num">{formatVND(l.agreedSalary)}</td>
                    <td className="pdp2-num">{formatVND(l.grossAmount - l.allowanceTaxFree - l.allowanceTaxable - l.bonusAmount)}</td>
                    <td className="pdp2-num"><button type="button" className="pdp2-bonus-cell" onClick={() => { setEditingBonusLine(l); setIndividualBonus(String(l.bonusAmount)); setIndividualBonusNote(''); }}>{formatVND(l.bonusAmount)} <span>✎</span></button></td>
                    <td className="pdp2-num">{formatVND(metrics.insuranceSalary)}</td>
                    {taxFreeItems.map((item) => <td key={item.key} className="pdp2-num blue">{formatVND(item.value)}</td>)}
                    <td className="pdp2-num blue">{formatVND(l.allowanceTaxable)}</td>
                    <td className="pdp2-num amber">{formatVND(metrics.bhxhCompany)}</td>
                    <td className="pdp2-num amber">{formatVND(metrics.bhytCompany)}</td>
                    <td className="pdp2-num amber">{formatVND(metrics.bhtnCompany)}</td>
                    <td className="pdp2-num amber strong">{formatVND(metrics.companyInsurance)}</td>
                    <td className="pdp2-num red">{formatVND(l.bhxhNv)}</td>
                    <td className="pdp2-num red">{formatVND(l.bhytNv)}</td>
                    <td className="pdp2-num red">{formatVND(l.bhtnNv)}</td>
                    <td className="pdp2-num red strong">{formatVND(bhNvTotal)}</td>
                    <td className="pdp2-num">{formatVND(metrics.taxableIncome)}</td>
                    <td className="pdp2-num purple">{formatVND(metrics.personalDeduction)}</td>
                    <td className="pdp2-num purple">{metrics.dependentCount}</td>
                    <td className="pdp2-num purple">{formatVND(metrics.dependentDeduction)}</td>
                    <td className="pdp2-num purple">{formatVND(metrics.employeeInsurance)}</td>
                    <td className="pdp2-num purple strong">{formatVND(metrics.deductions)}</td>
                    <td className="pdp2-num">{formatVND(metrics.assessableIncome)}</td>
                    <td className="pdp2-num red">{formatVND(l.personalIncomeTax)}</td>
                    <td className="pdp2-num pdp2-advance-cell">
                      {l.advanceDeduct > 0 ? (
                        <button type="button" className="pdp2-advance-sync" onClick={() => openLineDialog(l, 'advance')} title="Xem ứng lương đã đồng bộ">
                          <span>{formatVND(l.advanceDeduct)}</span><em>AUTO · 1 PHIẾU</em>
                        </button>
                      ) : <button type="button" className="pdp2-advance-empty" onClick={() => openLineDialog(l, 'advance')} title="Nhập tạm ứng thủ công">0đ <i>—</i></button>}
                    </td>
                    <td className="pdp2-num strong">{formatVND(metrics.totalSalary)}</td>
                    <td className="pdp2-num net">{formatVND(l.netAmount)}</td>
                    <td className="pdp2-num gray">0đ</td>
                    <td className="pdp2-row-action" onClick={(event) => event.stopPropagation()}>
                      <TableActionMenu
                        open={actionMenuId === l.id}
                        onToggle={() => setActionMenuId((current) => current === l.id ? null : l.id)}
                        onClose={() => setActionMenuId(null)}
                        ariaLabel={`Thao tác ${l.employeeName}`}
                        items={[
                          { label: 'Xem chi tiết', icon: '◉', onClick: () => openLineDialog(l, 'detail') },
                          { label: 'Điều chỉnh thủ công', icon: '✎', onClick: () => openLineDialog(l, 'adjust') },
                          { label: 'Xuất phiếu lương PDF', icon: '▧', onClick: () => openLineDialog(l, 'pdf') },
                          { label: 'Xem lịch sử lương', icon: '▥', onClick: () => openLineDialog(l, 'history') },
                          { label: 'Loại khỏi kỳ này', icon: '⌫', tone: 'danger', dividerBefore: true, onClick: () => openLineDialog(l, 'exclude') },
                        ] satisfies TableActionMenuItem[]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="pdp2-lines-total">
                  <td colSpan={4}>Tổng cộng · {filtered.length} NV</td>
                  <td className="pdp2-num">{formatVND(filtered.reduce((s, l) => s + l.baseSalary, 0))}</td>
                  <td className="pdp2-num">{formatVND(filtered.reduce((s, l) => s + l.agreedSalary, 0))}</td>
                  <td className="pdp2-num gray">{filtered.reduce((s, l) => s + Number(l.workingDays ?? 0), 0)}</td>
                  <td className="pdp2-num gray">{filtered.reduce((s, l) => s + Number(l.otHours ?? 0), 0)}</td>
                  <td className="pdp2-num strong">{filtered.reduce((s, l) => s + Number(l.workingDays ?? 0) + Number(l.otHours ?? 0), 0)}</td>
                  <td className="pdp2-num income-total">{formatVND(filtered.reduce((s, l) => s + l.grossAmount, 0))}</td>
                  <td className="pdp2-num">{formatVND(filtered.reduce((s, l) => s + l.agreedSalary, 0))}</td>
                  <td className="pdp2-num">{formatVND(filtered.reduce((s, l) => s + l.grossAmount - l.allowanceTaxFree - l.allowanceTaxable - l.bonusAmount, 0))}</td>
                  <td className="pdp2-num">{formatVND(filtered.reduce((s, l) => s + l.bonusAmount, 0))}</td>
                  <td className="pdp2-num">{formatVND(sumMetric('insuranceSalary'))}</td>
                  {TAX_FREE_ALLOWANCE_COLUMNS.map(([key]) => (
                    <td key={key} className="pdp2-num blue">{formatVND(filtered.reduce((s, l) => s + taxFreeBreakdown(l.allowanceTaxFree).find((item) => item.key === key)!.value, 0))}</td>
                  ))}
                  <td className="pdp2-num blue">{formatVND(filtered.reduce((s, l) => s + l.allowanceTaxable, 0))}</td>
                  <td className="pdp2-num amber">{formatVND(sumMetric('bhxhCompany'))}</td>
                  <td className="pdp2-num amber">{formatVND(sumMetric('bhytCompany'))}</td>
                  <td className="pdp2-num amber">{formatVND(sumMetric('bhtnCompany'))}</td>
                  <td className="pdp2-num amber strong">{formatVND(sumMetric('companyInsurance'))}</td>
                  <td className="pdp2-num red">{formatVND(filtered.reduce((s, l) => s + l.bhxhNv, 0))}</td>
                  <td className="pdp2-num red">{formatVND(filtered.reduce((s, l) => s + l.bhytNv, 0))}</td>
                  <td className="pdp2-num red">{formatVND(filtered.reduce((s, l) => s + l.bhtnNv, 0))}</td>
                  <td className="pdp2-num red strong">{formatVND(sumMetric('employeeInsurance'))}</td>
                  <td className="pdp2-num">{formatVND(sumMetric('taxableIncome'))}</td>
                  <td className="pdp2-num purple">{formatVND(sumMetric('personalDeduction'))}</td>
                  <td className="pdp2-num purple">{sumMetric('dependentCount')}</td>
                  <td className="pdp2-num purple">{formatVND(sumMetric('dependentDeduction'))}</td>
                  <td className="pdp2-num purple">{formatVND(sumMetric('employeeInsurance'))}</td>
                  <td className="pdp2-num purple strong">{formatVND(sumMetric('deductions'))}</td>
                  <td className="pdp2-num">{formatVND(sumMetric('assessableIncome'))}</td>
                  <td className="pdp2-num red">{formatVND(filtered.reduce((s, l) => s + l.personalIncomeTax, 0))}</td>
                  <td className="pdp2-num gray">{formatVND(filtered.reduce((s, l) => s + l.advanceDeduct, 0))}</td>
                  <td className="pdp2-num strong">{formatVND(sumMetric('totalSalary'))}</td>
                  <td className="pdp2-num net">{formatVND(filtered.reduce((s, l) => s + l.netAmount, 0))}</td>
                  <td className="pdp2-num gray">0đ</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {selectedLine && lineDialog && <PayrollLineActionModal line={selectedLine} payrollId={payrollId} action={lineDialog} onClose={() => setLineDialog(null)} onUpdated={loadLines} />}
      {editingBonusLine && <div className="pdp2-line-dialog-backdrop" onMouseDown={() => setEditingBonusLine(null)}><section className="pdp2-line-dialog" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><button type="button" className="pdp2-line-dialog-close" onClick={() => setEditingBonusLine(null)}>×</button><h3>Sửa thưởng cá nhân</h3><p>{editingBonusLine.employeeName}</p><div className="pdp2-line-dialog-grid"><span>Thưởng hiện tại<b>{formatVND(editingBonusLine.bonusAmount)}</b></span></div><label className="pdp2-line-input">Giá trị thưởng mới (VND)<input inputMode="numeric" value={individualBonus} onChange={(event) => setIndividualBonus(event.target.value)} /></label><label className="pdp2-line-input">Ghi chú (lý do điều chỉnh)<textarea value={individualBonusNote} onChange={(event) => setIndividualBonusNote(event.target.value)} placeholder="VD: Thưởng dự án hoàn thành" /></label><div className="pdp2-line-dialog-actions"><button type="button" onClick={() => setEditingBonusLine(null)}>Hủy</button><button type="button" className="primary" onClick={async () => { const amount = Number(individualBonus.replace(/\D/g, '')); if (!Number.isFinite(amount) || amount < 0) return setErr('Nhập giá trị thưởng hợp lệ.'); setSavingBonus(true); try { await payrollsApi.applyBulkBonus(payrollId, [editingBonusLine.id], amount); setEditingBonusLine(null); loadLines(); } catch (error) { setErr(error instanceof Error ? error.message : String(error)); } finally { setSavingBonus(false); } }} disabled={savingBonus}>{savingBonus ? 'Đang lưu…' : 'Lưu thưởng'}</button></div></section></div>}
    </div>
  );
}

function PayrollLineActionModal({ line, payrollId, action, onClose, onUpdated }: { line: PayrollLine; payrollId: string; action: PayrollLineAction; onClose: () => void; onUpdated: () => void }) {
  const title = { detail: 'Chi tiết lương nhân viên', adjust: 'Điều chỉnh thủ công', pdf: 'Xuất phiếu lương PDF', history: 'Lịch sử lương', exclude: 'Loại khỏi kỳ lương', advance: 'Ứng lương đã đồng bộ' }[action];
  const [advanceAmount, setAdvanceAmount] = useState(String(line.advanceDeduct || ''));
  const [advanceNote, setAdvanceNote] = useState(line.advanceNote || '');
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [savingAdvance, setSavingAdvance] = useState(false);
  const goToSalary = () => { window.location.assign(`/hr?employeeId=${encodeURIComponent(line.employeeId)}`); };
  const saveAdvance = async () => {
    const amount = Number(advanceAmount.replace(/\D/g, ''));
    if (!Number.isFinite(amount) || amount < 0 || !advanceNote.trim()) { setAdvanceError('Nhập số tiền và lý do điều chỉnh.'); return; }
    setSavingAdvance(true); setAdvanceError(null);
    try { await payrollsApi.setLineAdvance(payrollId, line.id, amount, advanceNote); onUpdated(); onClose(); }
    catch (error) { setAdvanceError(error instanceof Error ? error.message : String(error)); }
    finally { setSavingAdvance(false); }
  };
  return <div className="pdp2-line-dialog-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="pdp2-line-dialog" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
      <button type="button" className="pdp2-line-dialog-close" aria-label="Đóng" onClick={onClose}>×</button>
      <h3>{title}</h3><p>{line.employeeName}</p>
      {action === 'detail' && <div className="pdp2-line-dialog-grid"><span>Tổng thu nhập<b>{formatVND(line.grossAmount)}</b></span><span>Thực lãnh<b>{formatVND(line.netAmount)}</b></span><span>Ngày công<b>{line.workingDays ?? 0}</b></span><span>OT<b>{line.otHours ?? 0}</b></span></div>}
      {action === 'adjust' && <p className="pdp2-line-dialog-note">Mở cấu hình lương nhân viên để tạo mức lương có hiệu lực đúng kỳ, không làm thay đổi bảng lương đã chốt.</p>}
      {action === 'pdf' && <p className="pdp2-line-dialog-note">Phiếu lương sẽ được xuất theo số liệu của nhân viên trong kỳ hiện tại.</p>}
      {action === 'history' && <p className="pdp2-line-dialog-note">Lịch sử mức lương được quản lý tại Lương nhân viên.</p>}
      {action === 'exclude' && <p className="pdp2-line-dialog-note">Tính năng loại nhân viên cần API cập nhật danh sách nhân viên của bảng lương. Hiện chưa loại dữ liệu để tránh mất thông tin không thể khôi phục.</p>}
      {action === 'advance' && <><div className="pdp2-line-dialog-grid"><span>Đã đồng bộ từ phiếu Ứng lương<b>{formatVND(line.advanceDeduct)}</b></span><span>Trạng thái<b className="pdp2-dialog-success">AUTO · Đã đối chiếu</b></span></div><label className="pdp2-line-input">Nhập tay số tiền ứng lương<input inputMode="numeric" value={advanceAmount} onChange={(event) => setAdvanceAmount(event.target.value)} placeholder="VD: 3.000.000" /></label><label className="pdp2-line-input">Lý do điều chỉnh <span>*</span><textarea value={advanceNote} onChange={(event) => setAdvanceNote(event.target.value)} placeholder="VD: Phiếu ứng lương ngoài hệ thống" /></label>{advanceError && <p className="pdp2-line-dialog-error">{advanceError}</p>}</>}
      <div className="pdp2-line-dialog-actions"><button type="button" onClick={onClose}>Đóng</button>{action === 'adjust' || action === 'history' ? <button type="button" className="primary" onClick={goToSalary}>Mở Lương nhân viên</button> : action === 'pdf' ? <button type="button" className="primary" onClick={() => window.print()}>In / Lưu PDF</button> : action === 'advance' ? <button type="button" className="primary" disabled={savingAdvance} onClick={saveAdvance}>{savingAdvance ? 'Đang lưu…' : 'Lưu tạm ứng'}</button> : null}</div>
    </section>
  </div>;
}

/* ============ TAB: Công thức ============ */

export function TabFormula({ payroll }: { payroll: Payroll }) {
  const periodMonth = payroll.period.replace('T', 'Tháng ');
  const allowances = [
    ['AC', 'Ăn ca', 'Có · ≤730k', 'Không', '730.000đ', true],
    ['DT', 'Điện thoại', 'Có · theo QC', 'Không', '500.000đ', true],
    ['MT', 'Máy tính (thiết bị làm việc)', 'Có', 'Không', '1.500.000đ', true],
    ['XX', 'Xăng xe / Đi lại', 'Có', 'Không', '500.000đ', true],
    ['TT', 'Thưởng theo tuần / dự án', 'Không', 'Không', 'Nhập tay', true],
    ['TX', 'Trang phục', 'Có · 5tr/năm', 'Không', '400.000đ', false],
  ];
  const brackets = [['1', '≤ 5M', '5%'], ['2', '5M – 10M', '10%'], ['3', '10M – 18M', '15%'], ['4', '18M – 32M', '20%'], ['5', '32M – 52M', '25%'], ['6', '52M – 80M', '30%'], ['7', '> 80M', '35%']];
  return (
    <div className="pdp2-formula-page">
      <div className="pdp2-formula-banner">
        <div><b>Xem công thức áp dụng</b><span>· Chế độ chỉ đọc</span></div>
        <p>Công thức là setting toàn công ty · Muốn thay đổi phải vào Cài đặt · Chi phí lương</p>
        <button type="button" className="pdp2-detail-tool" onClick={() => window.location.assign('/settings?tab=salary')}>Đi tới Cài đặt</button>
      </div>
      <div className="pdp2-card pdp2-formula-card">
        <div className="pdp2-formula-title-row"><div><h3>Công thức tính lương áp dụng <span>v2026.07</span></h3><p>Cập nhật lần cuối 01/07/2026 · Theo TT111/2013 & Nghị định 74/2024</p></div></div>
        <div className="pdp2-formula-warning"><b>Cần đối chiếu công thức</b><span>Kế toán vui lòng đối chiếu với văn bản pháp luật hiện hành trước khi áp dụng kỳ lương mới. Sửa công thức yêu cầu KTT duyệt.</span></div>
        <div className="pdp2-formula-section-head"><h3>Tham số áp dụng</h3><span>Read-only · Sửa tại Cài đặt · Chi phí lương</span></div>
        <div className="pdp2-formula-params">
          <div><span>Ngày công chuẩn</span><b>21 ngày</b><small>Theo lịch {periodMonth}</small></div>
          <div><span>LTT vùng I</span><b>4.960.000đ</b><small>NĐ 74/2024/NĐ-CP</small></div>
          <div><span>Giảm trừ bản thân</span><b>11.000.000đ</b><small>NQ 954/2020/UBTVQH14</small></div>
          <div><span>Giảm trừ NPT</span><b>4.400.000đ</b><small>/ người phụ thuộc</small></div>
        </div>
        <div className="pdp2-formula-section-head"><h3>Loại phụ cấp cố định</h3><span>6 loại · dùng cho auto-fill khi tính lương</span></div>
        <div className="pdp2-lines-wrap"><table className="pdp2-lines-tbl pdp2-formula-table"><thead><tr><th>Mã</th><th>Tên phụ cấp</th><th>Miễn thuế</th><th>Đóng BH</th><th>Mức gợi ý mặc định</th><th>Trạng thái</th></tr></thead><tbody>{allowances.map(([code, name, exempt, insurance, amount, active]) => <tr key={String(code)}><td><span className="pdp2-acct-code">{code}</span></td><td>{name}</td><td><span className={exempt === 'Không' ? 'pdp2-formula-no' : 'pdp2-formula-yes'}>{exempt}</span></td><td>{insurance}</td><td className="pdp2-num">{amount}</td><td><span className={`pdp2-formula-toggle ${active ? 'on' : ''}`}>{active ? 'Đang bật' : 'Tắt'}</span></td></tr>)}</tbody></table></div>
        <p className="pdp2-formula-caption">Phụ cấp miễn thuế không tính vào TN chịu thuế TNCN · Phụ cấp đóng BH tính vào lương đóng bảo hiểm.</p>
        <div className="pdp2-formula-logic-grid">
          <section className="pdp2-formula-logic blue"><div className="pdp2-formula-letter">A</div><h3>Thu nhập trong kỳ</h3><div><b>Ngày OT quy đổi</b><code>Ngày OT = Số giờ OT × 24 / 8 × 1.5</code></div><div><b>Tổng ngày công thực tế</b><code>TDCT = Ngày đi làm + Ngày OT quy đổi</code></div><div><b>Lương theo ngày</b><code>Lương/ngày = Lương thoả thuận / Ngày công chuẩn</code></div><div><b>Tổng thu nhập</b><code>A = Lương thực tế + Phụ cấp + Thưởng</code></div></section>
          <section className="pdp2-formula-logic red"><div className="pdp2-formula-letter">B</div><h3>Bảo hiểm & Thuế</h3><div><b>Lương đóng bảo hiểm</b><code>L_BH = MIN(Lương thoả thuận, 20 × LTT vùng)</code></div><div className="pdp2-formula-rate"><span>BHXH <b>8%</b></span><span>BHYT <b>1.5%</b></span><span>BHTN <b>1%</b></span></div><div><b>Thu nhập tính thuế TNCN</b><code>TN chịu thuế − BH NV − 11tr − 4.4tr × NPT</code></div></section>
          <section className="pdp2-formula-logic green"><div className="pdp2-formula-letter">C</div><h3>Công ty đóng</h3><div className="pdp2-formula-rate four"><span>BHXH <b>17%</b></span><span>BHYT <b>3%</b></span><span>BHTN <b>1%</b></span><span>KPCĐ <b>2%</b></span></div><div><b>Tổng công ty đóng</b><code>= L_BH × 23%</code></div></section>
          <section className="pdp2-formula-logic purple"><div className="pdp2-formula-letter">D</div><h3>Thực nhận (Net)</h3><div><b>Net = A − BH NV − TNCN − Tạm ứng</b><code>Với lương NET, doanh nghiệp gánh phần thuế theo thoả thuận.</code></div><small>Nếu thu nhập tính thuế ≤ 0 thì TNCN = 0.</small></section>
        </div>
        <div className="pdp2-formula-section-head"><h3>Thuế TNCN theo biểu lũy tiến</h3><span>Áp dụng cho nhân sự chính thức</span></div>
        <div className="pdp2-lines-wrap pdp2-formula-tax-wrap"><table className="pdp2-lines-tbl pdp2-formula-tax"><thead><tr><th>Bậc</th><th>TN tính thuế / tháng</th><th>Thuế suất</th></tr></thead><tbody>{brackets.map((row) => <tr key={row[0]}>{row.map((cell) => <td key={cell}>{cell}</td>)}</tr>)}</tbody></table></div>
        <div className="pdp2-formula-history"><div className="pdp2-formula-section-head"><h3>Lịch sử thay đổi công thức</h3><button type="button">Xem đầy đủ →</button></div><div><b>01/07/2026</b><span>Cập nhật LTT vùng I lên 4.960.000đ theo Nghị định 74/2024/NĐ-CP · Lê Huỳnh Diệu</span></div><div><b>01/01/2026</b><span>Áp dụng biểu thuế TNCN theo TT111/2013 (7 bậc lũy tiến) · Nguyễn Thị Hà</span></div></div>
        </div>
      </div>
  );
}

/* ============ TAB: Trình ký ============ */

export function TabSignature({ payroll }: { payroll: Payroll }) {
  const [lines, setLines] = useState<PayrollLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setErr(null);
    payrollsApi.lines(payroll.id).then((result) => setLines(result.items))
      .catch((error) => setErr(error instanceof Error ? error.message : String(error)))
      .finally(() => setLoading(false));
  }, [payroll.id]);

  const employeeMeta: Record<string, { role: string; account?: string; bank?: string }> = {
    'Lê Huỳnh Diệu': { role: 'NV Kế toán tổng hợp' },
    'Lương Thị Yến': { role: 'Senior Developer' },
    'Nguyễn Văn F': { role: 'Freelance Dev' },
    'Phạm Minh Đức': { role: 'CEO' },
    'Trần Minh Khôi': { role: 'Junior Developer' },
    'Trần Thị B': { role: 'Marketing Lead' },
    'Đỗ Văn G': { role: 'NV Sản xuất' },
  };
  const kttDone = ['PENDING_CEO', 'APPROVED', 'DISBURSED'].includes(payroll.status);
  const ceoDone = ['APPROVED', 'DISBURSED'].includes(payroll.status);
  const signers = [
    { title: 'Người lập biểu', hint: '(Ký, ghi rõ họ tên)', name: payroll.createdBy, done: true },
    { title: 'Kế toán trưởng', hint: '(Ký, ghi rõ họ tên)', name: 'Nguyễn Thị Hà', done: kttDone },
    { title: 'Giám đốc', hint: '(Ký, ghi rõ họ tên)', name: 'Phạm Minh Đức', done: ceoDone },
  ];

  return (
    <div className="pdp2-card pdp2-signature-card">
      <div className="pdp2-signature-toolbar">
        <button type="button" className="pdp2-detail-tool" onClick={() => window.print()}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect width="12" height="8" x="6" y="14" /><path d="M6 9V2h12v7" /></svg>
          In để trình ký
        </button>
      </div>
      <div className="pdp2-signature-title">
        <h2>Bảng chi lương nhân viên</h2>
        <p>{payroll.period} — Bộ phận {payroll.department}</p>
      </div>
      {err && <div className="cvm-error" style={{ marginBottom: 12 }}>{err}</div>}
      {loading ? <div className="pay-empty">Đang tải danh sách chi lương…</div> : (
        <div className="pdp2-lines-wrap pdp2-signature-table-wrap">
          <table className="pdp2-lines-tbl pdp2-signature-table">
            <thead><tr><th>STT</th><th>Họ tên</th><th>Chức vụ</th><th>Tổng lương thực nhận</th><th>Vay VPBank</th><th>Vay MBBank</th><th>Lệnh lẻ</th><th>Số TK</th><th>Tên NH</th><th>Ghi chú</th></tr></thead>
            <tbody>{lines.map((line, index) => {
              const meta = employeeMeta[line.employeeName] ?? { role: 'Nhân viên' };
              return <tr key={line.id}>
                <td>{index + 1}</td><td><b>{line.employeeName}</b></td><td>{meta.role}</td><td className="pdp2-num strong">{formatVND(line.netAmount)}</td>
                <td>—</td><td>—</td><td>—</td><td>{meta.account ?? '—'}</td><td>{meta.bank ?? '—'}</td><td>—</td>
              </tr>;
            })}</tbody>
            <tfoot><tr><td colSpan={3}>Tổng cộng</td><td className="pdp2-num net">{formatVND(payroll.net)}</td><td colSpan={6} /></tr></tfoot>
          </table>
        </div>
      )}
      <div className="pdp2-signatures">
        {signers.map((signer) => <div className="pdp2-signature-person" key={signer.title}>
          <b>{signer.title}</b><span>{signer.hint}</span><div className={`pdp2-signature-mark ${signer.done ? 'signed' : ''}`}>{signer.done ? '✓' : ''}</div><strong>{signer.name}</strong>
        </div>)}
      </div>
      <div className={`pdp2-signature-status ${payroll.status === 'REJECTED' ? 'rejected' : payroll.status === 'DISBURSED' ? 'done' : ''}`}>
        {payroll.status === 'DRAFT' ? 'Bản nháp — gửi KTT để bắt đầu trình ký.' : payroll.status === 'PENDING_KTT' ? 'Đang chờ Kế toán trưởng duyệt.' : payroll.status === 'PENDING_CEO' ? 'Kế toán trưởng đã duyệt — đang chờ CEO ký.' : payroll.status === 'REJECTED' ? 'Bảng lương đã bị từ chối, cần điều chỉnh và gửi lại.' : payroll.status === 'DISBURSED' ? 'Đã hoàn tất ký và chi lương.' : 'Đã ký duyệt — sẵn sàng chi lương.'}
      </div>
    </div>
  );
}

/* ============ TAB: Hạch toán ============ */

export function TabAccounting({ payroll }: { payroll: Payroll }) {
  const { basicSalary, gross, bhEmployee, tncn, net, bhCompany } = payroll;
  const [monthPart, yearPart] = payroll.period.split('/');
  const journalCode = `PKT-${yearPart}-${monthPart.replace('T', '').padStart(2, '0')}-001`;
  // Split BH company 23% cho từng loại
  const bhxhDn = Math.round(basicSalary * 0.17);
  const bhytDn = Math.round(basicSalary * 0.03);
  const bhtnDn = Math.round(basicSalary * 0.01);
  const kpcdDn = Math.round(basicSalary * 0.02);
  // Split BH employee 10.5%
  const bhxhNv = Math.round(basicSalary * 0.08);
  const bhytNv = Math.round(basicSalary * 0.015);
  const bhtnNv = Math.round(basicSalary * 0.01);

  const entries = [
    { debit: '6421', debitName: 'Chi phí nhân viên QLDN', credit: '334', creditName: 'Phải trả người lao động', amount: gross, note: 'Chi phí lương gộp (bút toán tính lương)' },
    { debit: '6421', debitName: 'Chi phí NV QLDN — BH DN đóng', credit: '3383', creditName: 'BHXH DN đóng (17%)', amount: bhxhDn, note: 'DN nộp cho KBNN cùng NV' },
    { debit: '6421', debitName: 'Chi phí NV QLDN — BH DN đóng', credit: '3384', creditName: 'BHYT DN đóng (3%)', amount: bhytDn, note: '' },
    { debit: '6421', debitName: 'Chi phí NV QLDN — BH DN đóng', credit: '3386', creditName: 'BHTN DN đóng (1%)', amount: bhtnDn, note: '' },
    { debit: '6421', debitName: 'Chi phí NV QLDN — KPCĐ', credit: '3382', creditName: 'KPCĐ (2%)', amount: kpcdDn, note: '' },
    { debit: '334', debitName: 'Phải trả NV — BH NV chịu', credit: '3383', creditName: 'BHXH NV chịu (8%)', amount: bhxhNv, note: 'Trừ vào lương NV' },
    { debit: '334', debitName: 'Phải trả NV — BH NV chịu', credit: '3384', creditName: 'BHYT NV chịu (1.5%)', amount: bhytNv, note: '' },
    { debit: '334', debitName: 'Phải trả NV — BH NV chịu', credit: '3386', creditName: 'BHTN NV chịu (1%)', amount: bhtnNv, note: '' },
    { debit: '334', debitName: 'Phải trả NV — Thuế TNCN', credit: '3335', creditName: 'Thuế TNCN phải nộp', amount: tncn, note: 'Trừ tại nguồn' },
    ...(payroll.status === 'DISBURSED' ? [{ debit: '334', debitName: 'Phải trả NV — Chi lương', credit: '1121', creditName: 'Tiền gửi ngân hàng', amount: net, note: 'Bút toán chi lương' }] : []),
  ];

  const totalDebit = entries.reduce((s, e) => s + e.amount, 0);
  const totalCredit = totalDebit;

  return (
    <div className="pdp2-card pdp2-acct-card">
      <div className="pdp2-acct-head">
        <div>
          <div className="pdp2-acct-doc"><b>{journalCode}</b><span>{payroll.status === 'DISBURSED' ? 'Đã post' : 'Nháp · chưa post'}</span></div>
          <p>Hạch toán chi phí lương {payroll.period} · bộ phận {payroll.department} · {payroll.employeeCount} NV · tự sinh từ {payroll.code}</p>
        </div>
        <div className="pdp2-acct-summary">
          <div><span>Tổng Nợ</span><b>{formatVND(totalDebit)}</b></div>
          <div><span>Tổng Có</span><b>{formatVND(totalCredit)}</b></div>
        </div>
      </div>
      <div className="pdp2-acct-default"><b>Bút toán mặc định theo TT200</b><span>TK 6421 cho bộ phận {payroll.department} · TK 334 đối ứng · TK 3383/3384/3386 cho bảo hiểm. Định khoản lấy từ cấu hình Hệ thống tài khoản.</span></div>
      <div className="pdp2-acct-fields"><div><span>Loại chứng từ</span><b>PKT · Phiếu kế toán</b></div><div><span>Ngày chứng từ</span><b>Cuối kỳ {payroll.period}</b></div><div><span>Kỳ kế toán</span><b>{payroll.period}</b></div><div><span>Diễn giải chung</span><b>Hạch toán chi phí lương {payroll.period} · {payroll.department}</b></div></div>
      <div className="pdp2-acct-section"><h3>Định khoản</h3><span>{entries.length} dòng · Nợ = Có = {formatVND(totalDebit)}</span></div>

      <div className="pdp2-lines-wrap">
        <table className="pdp2-lines-tbl pdp2-acct-tbl">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th style={{ width: 80 }}>Nợ TK</th>
              <th>Diễn giải Nợ</th>
              <th style={{ width: 80 }}>Có TK</th>
              <th>Diễn giải Có</th>
              <th style={{ width: 140, textAlign: 'right' }}>Số tiền</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i}>
                <td style={{ color: '#94a3b8' }}>{i + 1}</td>
                <td><span className="pdp2-acct-code">{e.debit}</span></td>
                <td>{e.debitName}{e.note && <span className="pdp2-acct-note"> · {e.note}</span>}</td>
                <td><span className="pdp2-acct-code">{e.credit}</span></td>
                <td>{e.creditName}</td>
                <td className="pdp2-num strong">{formatVND(e.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={5}>Tổng cộng · Nợ = Có · Cân đối ✓</td><td className="pdp2-num net">{formatVND(totalDebit)}</td></tr></tfoot>
        </table>
      </div>

      <div className="pdp2-acct-impact"><h3>Ảnh hưởng số dư tài khoản</h3><div className="pdp2-acct-impact-grid"><div><span>6421 · Chi phí quản lý DN</span><b>+{formatVND(gross + bhCompany)}</b></div><div><span>334 · Phải trả người lao động</span><b>+{formatVND(net)}</b></div><div><span>3383/3384/3386 · Bảo hiểm phải nộp</span><b>+{formatVND(bhEmployee + bhCompany)}</b></div><div><span>3335 · Thuế TNCN phải nộp</span><b>+{formatVND(tncn)}</b></div></div></div>
      <div className="pdp2-acct-check"><h3>Kiểm tra tự động</h3><ul><li>✓ Nợ = Có · chứng từ cân đối</li><li>✓ Số tiền khớp bảng lương</li><li>✓ Tài khoản đối ứng hợp lệ</li><li>{payroll.status === 'DISBURSED' ? '✓ Đã chi lương · có thể post lên Sổ cái' : '⌛ Chờ chi lương trước khi post lên Sổ cái'}</li></ul></div>

      <div className="pdp2-acct-status">
        <div className={`pdp2-acct-status-pill pdp2-acct-status-${payroll.status === 'DISBURSED' ? 'done' : 'draft'}`}>
          {payroll.status === 'DISBURSED' ? '✓ Đã ghi sổ' : '⌛ Chờ chi lương → ghi sổ'}
        </div>
        <span style={{ color: '#64748b', fontSize: 12 }}>
          {payroll.status === 'DISBURSED'
            ? `Ghi sổ tự động vào ngày chi ${payroll.disbursedAt ? formatDate(payroll.disbursedAt) : ''} · Trạng thái: ${STATUS_LABEL[payroll.status]}`
            : `Bút toán chi lương (Nợ 334/Có 1121) chỉ ghi khi bảng lương chuyển sang DISBURSED · Hiện: ${STATUS_LABEL[payroll.status]}`}
        </span>
      </div>
    </div>
  );
}
